import { useState, useEffect, useContext } from 'react'
import { useT } from '../hooks'
import AuthContext from 'contexte/AuthContext'
import { launchError, launchSuccess } from 'components/notifications'
import { fetchBBookLimits, updateBBookLimits } from 'services/bBookServices'
import BBookAmountField from './BBookAmountField'

const FIELDS = ['max_daily_loss', 'max_profit_per_user', 'lmax_commission_pct', 'max_funded_margin', 'lmax_margin_reserve_eur']
const UNITS = { lmax_commission_pct: '%' } // the others are amounts in euros
const ZERO_ALLOWED = ['lmax_margin_reserve_eur'] // no reserve is a choice; no cap is not
const isValid = (value, field) => value !== '' && Number.isFinite(Number(value)) && (Number(value) > 0 || (Number(value) === 0 && ZERO_ALLOWED.includes(field))) && (field !== 'lmax_commission_pct' || Number(value) <= 1)
const empty = (fill) => Object.fromEntries(FIELDS.map((field) => [field, fill]))

// b-book:limits — house daily loss (sweeps every position), per-user profit cap (pairs one position), what LMAX charges per hedge order,
// and the margin the house may put up on LMAX for leverages above LMAX's (cap, and the cash to keep free on the account).
const BBookLimits = () => {
  const { t } = useT()
  const { user } = useContext(AuthContext)

  const [saved, setSaved] = useState(empty(null))
  const [values, setValues] = useState(empty(''))
  const [touched, setTouched] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchBBookLimits(user)
        setSaved(data)
        setValues(Object.fromEntries(FIELDS.map((field) => [field, data[field] ?? ''])))
      } catch (e) {
        launchError(t('notifications.b_book.limits.fetch_error'))
      }
    }
    load()
  }, [])

  const changed = FIELDS.filter((field) => String(values[field]) !== String(saved[field] ?? ''))
  const invalid = changed.filter((field) => !isValid(values[field], field))
  const errorOf = (field) => (touched[field] && invalid.includes(field) ? t(field === 'lmax_commission_pct' ? 'pages.b_book.limits.invalid_commission' : ZERO_ALLOWED.includes(field) ? 'pages.b_book.limits.invalid_zero_allowed' : 'pages.b_book.limits.invalid') : null)

  const save = async () => {
    if (invalid.length) {
      setTouched(Object.fromEntries(FIELDS.map((field) => [field, true])))
      return document.getElementById(`limit-${invalid[0]}`)?.focus() // first invalid field
    }
    try {
      setSaving(true)
      const data = await updateBBookLimits(user, Object.fromEntries(changed.map((field) => [field, Number(values[field])])))
      setSaved(prev => ({ ...prev, ...Object.fromEntries(changed.map((field) => [field, data[field]])) }))
      setTouched({})
      if (data.applied_live) launchSuccess(t('notifications.b_book.limits.success'))
      else launchError(t('notifications.b_book.engine_not_listening')) // stored, but only applied at the next engine start
    } catch (e) {
      launchError(t('notifications.b_book.limits.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <fieldset className='b_book_status_row b_book_form_row'>
      <legend className='b_book_status_label'><p>{t('pages.b_book.limits.label')}</p></legend>
      <div className='b_book_form_fields'>
        {FIELDS.map((field) => (
          <BBookAmountField
            key={field}
            id={`limit-${field}`}
            label={t(`pages.b_book.limits.${field}`)}
            helper={t(`pages.b_book.limits.${field}_hint`)}
            unit={UNITS[field] ?? '€'}
            placeholder={['max_daily_loss', 'max_profit_per_user'].includes(field) ? t('pages.b_book.limits.env_default') : undefined}
            value={values[field]}
            error={errorOf(field)}
            disabled={saving}
            onChange={(value) => setValues(prev => ({ ...prev, [field]: value }))}
            onBlur={() => setTouched(prev => ({ ...prev, [field]: true }))}
          />
        ))}
        <button className='b_book_action_button b_book_form_submit' disabled={saving || !changed.length} aria-busy={saving || undefined} onClick={save}>
          {saving ? t('pages.b_book.saving') : t('pages.b_book.exposure.save')}
        </button>
      </div>
    </fieldset>
  )
}

export default BBookLimits
